<template>
  <div
    :style="outerCardStyle"
  >
    <v-card
      class="card-chart"
      :class="cardClass"
      :style="checkCardStyle"
      @mouseover="mouseOver"
      @mouseleave="mouseLeave"
    >
      <div
        class="card-content"
        :class="card.props.noPadding ? 'pa-0' : 'pa-4'"
      >
        <slot
          v-bind="{ ...cardModalMethods }"
          :is-modal="false"
        />
      </div>

      <transition
        v-if="!showNewCardMenuFeature"
        name="slide-fade"
      >
        <div
          v-if="showExtraOptions && !card.hideMenu"
          class="card-extra-options"
        >
          <v-card
            height="40"
            class="px-3 card-extra-options__container"
          >
            <StIcon
              v-if="showExpandIcon"
              v-tooltip="expandCardLabel"
              class="pointer"
              icon-name="arrow-expand-all"
              @click="showExpandCardModal = true"
            />

            <StIcon
              v-tooltip="resizeCardLabel"
              class="pointer"
              icon-name="resize"
              @click="openResizeModal"
            />

            <StIcon
              v-if="isListeningChart"
              v-tooltip="filterCardLabel"
              :disabled="card.notSupportFilter"
              class="pointer"
              icon-name="filter"
              @click="openFilterListeningModal"
            />

            <StIcon
              v-if="isSacChart"
              v-tooltip="filterCardLabel"
              :disabled="card.notSupportFilter"
              class="pointer"
              icon-name="filter"
              @click="openFilterSacModal"
            />

            <StIcon
              v-if="isSacStatusChart"
              v-tooltip="filterCardLabel"
              :disabled="card.notSupportFilter"
              class="pointer"
              icon-name="filter"
              @click="openFilterSacStatusModal"
            />

            <StIcon
              v-if="isBlipChart"
              v-tooltip="filterCardLabel"
              :disabled="card.notSupportFilter"
              class="pointer"
              icon-name="filter"
              @click="openFilterBlipModal"
            />

            <StIcon
              v-if="isPageMetricsChart"
              v-tooltip="filterCardLabel"
              :disabled="card.notSupportFilter"
              class="pointer"
              icon-name="filter"
              @click="openFilterPageMetricsModal"
            />

            <StIcon
              v-if="isCompetitiveChart"
              v-tooltip="filterCardLabel"
              :disabled="card.notSupportFilter"
              class="pointer"
              icon-name="filter"
              @click="openFilterCompetitiveModal"
            />

            <StIcon
              v-if="isSacListeningChart"
              v-tooltip="filterCardLabel"
              :disabled="card.notSupportFilter"
              class="pointer"
              icon-name="filter"
              @click="openFilterSacListeningModal"
            />

            <StIcon
              v-tooltip="duplicateCardLabel"
              class="pointer"
              icon-name="content-duplicate"
              @click="duplicateCard"
            />

            <StIcon
              v-tooltip="deleteCardLabel"
              class="pointer"
              icon-name="delete"
              @click="deleteCard"
            />
          </v-card>
        </div>
      </transition>

      <template v-for="(modal, index) in cardOptionsModals">
        <DynamicModalComponent
          :key="index"
          :component-name="modal.componentName"
          :custom-props="modal.props"
          :events="modal.events"
        />
      </template>

      <v-dialog
        v-if="showExpandCardModal"
        v-model="showExpandCardModal"
        :width="modalMaxWidth"
        overlay-color="var(--ds-color-reverse)"
      >
        <v-card class="pa-5 expand-modal-card">
          <slot
            v-bind="{ ...expandedCardModalMethods }"
            :is-modal="true"
            :is-expanded-card="showExpandCardModal"
          />
        </v-card>
      </v-dialog>
    </v-card>
  </div>
</template>

<script>
import cardsModel from '@/mixins/cards-model';
import filtersModel from '@/mixins/filters-model';
import pagesModel from '@/mixins/pages-model';
import { mapMutations, mapState, mapActions } from 'vuex';
import { MUTATE } from '@/store/modules/mutation_types';
import { DATA_TYPE, HEIGHT } from '@/store/modules/cards/constants';
import DynamicModalComponent from '@/components/modal-model/DynamicModalComponent';
import EventBus from '@/utils/event-bus';
import proprietaryModelForFilter from '@/mixins/proprietary-model-for-filter';
import { Flags } from '@/plugins/rollout';
import StIcon from '@stilingue/st-icon';

export default {
  name: 'CardChart',
  components: {
    DynamicModalComponent,
    StIcon,
  },
  mixins: [
    cardsModel,
    filtersModel,
    proprietaryModelForFilter,
    pagesModel,
  ],
  props: {
    margin: {
      type: Number,
      default: 8,
    },
    isDisplayChart: {
      type: [Boolean, null],
      default: false,
    },
    width: {
      type: Number,
      default: 0,
    },
    height: {
      type: [Number, String],
      default: 0,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      widthWeight: 48,
      heightWeight: 50,
      duplicateCardLabel: 'Duplicar',
      filterCardLabel: 'Filtrar',
      resizeCardLabel: 'Redimensionar',
      deleteCardLabel: 'Remover',
      expandCardLabel: 'Expandir',
      showExtraOptions: false,
      padding: 16,
      isRemovingCard: false,
      showExpandIcon: false,
      flickingCard: false,
      applyControl: false,
      cardModalMethods: {
        expandCardModal: this.expandCardModal,
        openFilterListeningModal: this.openFilterListeningModal,
        openFilterSacModal: this.openFilterSacModal,
        openFilterPageMetricsModal: this.openFilterPageMetricsModal,
        openFilterCompetitiveModal: this.openFilterCompetitiveModal,
        openFilterSacStatusModal: this.openFilterSacStatusModal,
        openFilterSacListeningModal: this.openFilterSacListeningModal,
        openFilterBlipModal: this.openFilterBlipModal,
        openResizeModal: this.openResizeModal,
        duplicateCard: this.duplicateCard,
        deleteCard: this.deleteCard,
        showExpandCardModal: this.showExpandCardModal,
      },
      expandedCardModalMethods: {
        deleteCard: this.deleteCard,
        showExpandCardModal: this.showExpandCardModal,
      },
    };
  },
  computed: {
    ...mapState('filter', [
      'accountId',
    ]),
    ...mapState('dashboard', [
      'isStaging',
    ]),
    widthComputed() {
      const widthWithoutPadding = (this.width * 2) * this.widthWeight;
      const widthPadding = ((this.width * 2) - 1) * this.padding;
      return `${widthWithoutPadding + widthPadding}px`;
    },
    heightComputed() {
      if (this.height === HEIGHT.AUTO) {
        return HEIGHT.AUTO;
      }
      const heightWithoutPadding = this.height * this.heightWeight;
      const heightPadding = (this.height - 1) * this.padding;
      return `${heightWithoutPadding + heightPadding}px`;
    },
    cardClass() {
      return {
        'flick-card': this.flickingCard,
      };
    },
    outerCardStyle() {
      return {
        width: this.widthComputed,
        height: this.heightComputed,
      };
    },
    cardStyle() {
      return {
        'margin': `${this.margin}px`,
        'height': this.height === HEIGHT.AUTO
          ? HEIGHT.AUTO
          : `calc(100% - ${this.margin * 2}px)`,
      };
    },
    checkCardStyle() {
      if (this.card.dataType === DATA_TYPE.STATIC) {
        return { ...this.cardStyle, 'background-color': 'transparent !important' };
      }
      return this.cardStyle;
    },
    isListeningChart() {
      return this.card.dataType === DATA_TYPE.LISTENING;
    },
    isSacChart() {
      return this.card.dataType === DATA_TYPE.SAC;
    },
    isPageMetricsChart() {
      return this.card.dataType === DATA_TYPE.PAGE;
    },
    isCompetitiveChart() {
      return this.card.dataType === DATA_TYPE.COMPETITIVE;
    },
    isSacStatusChart() {
      return this.card.dataType === DATA_TYPE.SAC_STATUS;
    },
    isSacListeningChart() {
      return this.card.dataType === DATA_TYPE.SAC_LISTENING;
    },
    isBlipChart() {
      return this.card.dataType === DATA_TYPE.BLIP;
    },
    componentName() {
      return this.card.props.title || '';
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    modalMaxWidth() {
      return '90%';
    },
    getCurrentCardPosition() {
      return this.activeDashboard.data.cards.findIndex(card => card.id === this.card.id);
    },
    indexToInsertDuplicateCard() {
      return this.getCurrentCardPosition + 1;
    },
    showNewCardMenuFeature() {
      return this.isEnabled(Flags.showNewCardMenu);
    },
  },
  watch: {
    showExpandCardModal() {
      if (this.showExpandCardModal) {
        setTimeout(() => {
          this.card.props.isExpandedCard = this.showExpandCardModal;
        }, 150);
      } else {
        this.card.props.isExpandedCard = this.showExpandCardModal;
      }
    },
  },
  created() {
    if (this.card && !this.card.dataType) {
      const baseModel = this.allowCards.find(allowCard => allowCard.component === this.card.component);
      this.card.dataType = baseModel.dataType;
      this.saveCards();
    }

    EventBus.$on('updated', (payload) => {
      if (payload.itemId !== this.itemId) {
        return;
      }

      this.flickCard();
    });
  },
  methods: {
    ...mapActions('filter', [
      'applyGlobalFilter',
    ]),
    ...mapActions('dashboard', [
      'deleteImage',
      'duplicateImage',
    ]),
    ...mapMutations('misc', {
      mutateMisc: MUTATE,
    }),
    ...mapMutations('cards', {
      mutateCards: MUTATE,
    }),
    removeControlFilters() {
      if (this.applyControl) {
        delete this.card.props.filter.channel;
        delete this.card.props.filter.channels;
        delete this.card.props.filter.pages;
      }
    },
    flickCard() {
      this.flickingCard = true;

      setTimeout(() => {
        this.flickingCard = false;
      }, 2000);
    },
    mouseOver() {
      this.showExpandIcon = true;
      if (!this.shouldDisableChanges) {
        this.showExtraOptions = true;
      }
    },
    mouseLeave() {
      this.showExpandIcon = false;
      if (!this.shouldDisableChanges) {
        this.showExtraOptions = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .card-chart {
    box-shadow: none !important;
    border-radius: 8px;
  }

  .card-content {
    height: 100%;
    overflow: auto;
  }

  .pointer {
    cursor: pointer;
  }

  .card-extra-options {
    position: absolute;
    top: -20px;
    right: 0;
    height: 40px;
    z-index: 5;
  }

  .card-extra-options__container {
    display: flex;

    button {
      width: 40px;
    }
  }

  @media screen and (max-width: 600px) {
    .card-chart {
      max-width: 90vw !important;
    }
  }

  /* Enter and leave animations can use different durations and timing functions. */
  .slide-fade-enter-active {
    transition: all 0.3s ease-out;
  }

  .slide-fade-leave-active {
    transition: all 0.3s ease-in;
  }

  .slide-fade-enter,
  .slide-fade-leave-to {
    transform: translateY(10px);
    opacity: 0;
  }

  .expand-modal-card {
    min-height: 90vh;
    display: grid;
    grid-template-columns: 1fr;
    align-items: center;
    justify-content: center;
  }

  .flick-card {
    position: relative;
    overflow: hidden;
  }

  .flick-card::after {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    animation: flickCard 2s;
    background-color: #fff;
    opacity: 0;
    pointer-events: none;
    content: '';
  }

  @keyframes flickCard {
    from { opacity: 0.2; }
    to { opacity: 0; }
  }
</style>
